import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useEffect } from 'react';
// DTC-L: route Passport-sync `technician_type_inferred_at` write
// through the canonical clock for deterministic audit timestamps.
import { clock } from '@/utils/clock';

import { PassportSnapshotCard } from '@/components/passport/PassportSnapshotCard';
import { PassportEmploymentHistory } from '@/components/passport/PassportEmploymentHistory';
import { PassportProficiencySection } from '@/components/passport/PassportProficiencySection';
import { fetchLiveProficiencyClasses, portableProvenanceClassOf } from '@/lib/passport/proficiencyProvenance';
import { PassportSkillHeatmap } from '@/components/passport/PassportSkillHeatmap';
import { PassportPerformanceDNA } from '@/components/passport/PassportPerformanceDNA';
import { PassportTimeSignature } from '@/components/passport/PassportTimeSignature';
import { PassportPensieveInsights } from '@/components/passport/PassportPensieveInsights';
import { PassportPensieveCoaching } from '@/components/passport/PassportPensieveCoaching';
import { PassportPensieveSummary } from '@/components/passport/PassportPensieveSummary';
import { PassportTicketLedger } from '@/components/passport/PassportTicketLedger';
import { PassportFutureInsights } from '@/components/passport/PassportFutureInsights';
import { PassportCoachingNotes } from '@/components/passport/PassportCoachingNotes';
import { PassportLearningCenter } from '@/components/passport/PassportLearningCenter';
import { PassportDiagnosticCompetency } from '@/components/passport/PassportDiagnosticCompetency';
import { ToolboxSection } from '@/components/toolbox/ToolboxSection';
import { PassportInspectionMetrics } from '@/components/passport/PassportInspectionMetrics';
import { PassportRoleTracks } from '@/components/passport/PassportRoleTracks';
import { PassportCertificationsSection } from '@/components/passport/PassportCertificationsSection';
import { PassportExperienceSection } from '@/components/passport/PassportExperienceSection';
import { PassportEducationSection } from '@/components/passport/PassportEducationSection';
import { PassportReferencesSection } from '@/components/passport/PassportReferencesSection';
import { PassportEligibilitySection } from '@/components/passport/PassportEligibilitySection';

import { useDirectTechnicianMetrics } from '@/hooks/useDirectTechnicianMetrics';
import { TechnicianBackgroundCard } from '@/components/technicians/TechnicianBackgroundCard';
import { usePermissions } from '@/hooks/usePermissions';
import { useTechnicianPensieveStats } from '@/hooks/useTechnicianPensieveStats';
import { RequireFeature } from '@/components/access/RequireFeature';
import { useUserRole } from '@/hooks/useUserRole';
import { useAuth } from '@/contexts/AuthContext';
import { useIsOwnProfile } from '@/hooks/useToolbox';
import { resolveTechnicianTypeTitle } from '@/utils/resolveTechnicianTypeTitle';
import { PassportSection } from '@/components/passport/PassportSection';
import { resolveViewerAudiences } from '@/lib/passport/sectionAudience';

export default function TechnicianPassport() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Fetch snapshot technician profile
  const { data: snapshotProfile, isLoading: profileLoading } = useQuery({
    queryKey: ['technician-profile', id],
    queryFn: async () => {
      // Core-100 R4 W2 (CORE-Q): one server read. `open_technician_passport` returns the row the table's RLS allows
      // and, when it is someone else's Passport, records the view (with the server-computed basis) in the SAME
      // transaction — the person's record of who looked no longer depends on the browser asking for it.
      const { data, error } = await supabase.rpc('open_technician_passport' as never, { p_technician_profile_id: id! } as never);
      if (error) throw error;
      const result = data as unknown;
      const row = (Array.isArray(result) ? result[0] : result) as Database['public']['Tables']['technician_profiles']['Row'] | null | undefined;
      if (!row) throw new Error('Passport not found or not visible to you');
      return row;
    },
    enabled: !!id,
  });

  // Live overlay: check if this profile has an active linked technician
  const liveTechQuery = useQuery({
    queryKey: ['passport-live-tech-by-profile', id],
    queryFn: async () => {
      // Find technician_users link for this profile
      const { data: techs, error: techErr } = await supabase
        .from('technicians')
        .select('id, shop_id, name, proficiencies, employment_status')
        .eq('technician_profile_id', id!)
        .eq('employment_status', 'Active')
        .limit(1);

      if (techErr || !techs?.length) return null;

      // Verify the link still exists in technician_users
      const { data: link } = await supabase
        .from('technician_users')
        .select('id')
        .eq('technician_id', techs[0].id)
        .maybeSingle();

      return link ? techs[0] : null;
    },
    enabled: !!id,
  });
  const liveTech = liveTechQuery.data;

  // ── CORE-C GAP-005: settled identity, not merely a present id ──────────
  //
  // `liveTech?.id` is part of the proficiency query KEY, so before this
  // lookup resolved the query ran with the id absent (snapshot branch) and
  // again once it arrived (live branch): a duplicate round trip whose first
  // result is discarded, and a visible flip of the proficiency set under
  // the reader.
  //
  // Gating on `!!liveTech?.id` would be wrong - a subject with NO live
  // technician is a legitimate state that must still read the snapshot.
  // What the dependent query needs is that the lookup has SETTLED, which is
  // three-valued: unresolved / resolved-with / resolved-without.
  //
  // `isFetched` covers success AND error, so a failed lookup keeps today's
  // behaviour (fall through to the snapshot) rather than silently disabling
  // proficiency - failure semantics are GAP-003's and are not changed here.
  const liveIdentitySettled = !id || liveTechQuery.isFetched;

  // Merge: live tech overlays snapshot
  const profile = snapshotProfile ? {
    ...snapshotProfile,
    ...(liveTech ? { full_name: liveTech.name, is_active: true } : {}),
  } : null;

  // Fetch employment history
  const { data: employmentHistory } = useQuery({
    queryKey: ['technician-employment', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('technician_employment_history')
        .select(`
          *,
          shop:shops(name)
        `)
        .eq('technician_profile_id', id!)
        .order('start_date', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  // Fetch proficiencies — prefer live tech data if attached
  const { data: proficiencies } = useQuery({
    queryKey: ['technician-proficiencies', id, liveTech?.id],
    queryFn: async () => {
      if (liveTech?.proficiencies) {
        const liveProf = liveTech.proficiencies as Record<string, number>;
        // CORE-C GAP-007. `technicians.proficiencies` is a Record<string, number>:
        // it supplies a category and a score and NOTHING else. It used to be
        // adapted with `confidence_score: 0.5` and `sample_size: 0`, which put
        // "Confidence: 50%" and "0 jobs" on the person's Passport about work
        // nobody had measured. CORE-C hosts this fact; it does not own the
        // metadata, and a missing confidence is not low confidence.
        // Core-100 R5 (CORE-C F/Prov): the live map carries no class; CORE-E's authority does, by the same rule the
        // portable writer applies. A failed read leaves the class unknown (nothing is said), never guessed.
        const classes = await fetchLiveProficiencyClasses(
          supabase, liveTech.id, liveTech.shop_id, Object.keys(liveProf),
        );
        return Object.entries(liveProf).map(([key, score]) => ({
          category_key: key,
          proficiency_score: Math.round(score),
          confidence_score: null,
          sample_size: null,
          provenance_class: classes ? classes[key] ?? null : null,
        }));
      }
      const { data, error } = await supabase
        .from('technician_proficiencies')
        .select('*')
        .eq('technician_profile_id', id!);
      if (error) throw error;
      return data?.map(p => ({
        category_key: p.category_key,
        proficiency_score: p.proficiency_score,
        confidence_score: p.confidence_score,
        // `|| 0` turned a genuinely NULL sample size into a factual "0 jobs".
        // The column is nullable and the sync trigger never writes it, so null
        // is "we don't know how much work", not "no work". A real 0 survives.
        sample_size: p.sample_size ?? null,
        // Core-100 R5 (CORE-C F/Prov): the class the single portable writer recorded at rest.
        provenance_class: portableProvenanceClassOf((p as { provenance_class?: string | null }).provenance_class),
      })) || [];
    },
    enabled: !!id && liveIdentitySettled,
  });

  // Use direct metrics from tickets/ticket_jobs (single source of truth)
  const { data: metrics } = useDirectTechnicianMetrics(id || '');
  // Shop management may correct a technician's stated background; the server
  // (set_technician_trade_background) is the authority either way.
  const backgroundPerms = usePermissions();
  // Oct 20 addendum (ORG_ADMIN separation): an actual Owner/Manager of the active shop, multi-role aware. ORG_ADMIN
  // alone views the Passport read-only; set_technician_trade_background refuses it server-side too.
  const canEditBackground = backgroundPerms.hasRole('OWNER', 'MANAGER');

  // Pensieve learning stats (Catalyst+ only)
  const pensieveQuery = useTechnicianPensieveStats(id);

  // Get user role for manager-only sections (legacy scalar read; kept for its existing callers and loading state)
  const { role, isManagerView: scalarManagerView, isLoading: isRoleLoading } = useUserRole();
  // Oct 20 addendum P1/P2: also read the viewer's capacities in the ACTIVE shop multi-role (authority + work roles):
  // the scalar read alone walled out a Service Advisor whose scalar is TECHNICIAN. The SERVER decides who may open
  // this Passport (open_technician_passport / can_view_technician_passport); these only choose presentation.
  const isManagerView = scalarManagerView || backgroundPerms.hasRole('OWNER', 'MANAGER');
  // Read-only Passport audience besides the person: Owner/Manager, Service Advisor (the SERVICE_WRITER projection of
  // the SERVICE_ADVISOR work role), ORG_ADMIN — or, as before, any non-technician scalar role the route admits.
  const isPassportViewer = (!!role && role !== 'TECHNICIAN')
    || backgroundPerms.hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'ORG_ADMIN');
  // Core-100 R2 CORE-D: the shop a manager may confirm employment for.
  const { activeShop, user: viewer } = useAuth();

  // Ownership check for technicians viewing their own passport
  const { isOwnProfile, isLoading: isCheckingOwnership } = useIsOwnProfile(id);

  // CORE-C GAP-004: the audiences THIS viewer holds over THIS Passport,
  // resolved once and applied at every section boundary below. A viewer who
  // is not the subject and holds no Passport-viewing capacity is turned away
  // further down, so an in-scope viewer is exactly an Owner/Manager, Service
  // Advisor or ORG_ADMIN here (Oct 20 addendum P1/P2). That is a presentation
  // classification, not a claim that the two people share a shop or an
  // organisation — the server (can_view_technician_passport) decides that.
  const viewerAudiences = resolveViewerAudiences({
    isSelf: isOwnProfile,
    inScopeEmployer: isPassportViewer,
    isManager: isManagerView,
    isResolving: isCheckingOwnership || isRoleLoading,
  });

  // Fetch passport_profiles ONLY for own profile (RLS-safe: Guardrail #1)
  const { data: passportProfile } = useQuery({
    queryKey: ['passport-profile-for-title', profile?.user_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('passport_profiles')
        .select('technician_type_self_reported')
        .eq('user_id', profile!.user_id!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!profile?.user_id && isOwnProfile === true,
  });

  // Oct 20 addendum P1/P2 (WS-FIX3, F4): References and Eligibility are HR-private. Their rows are readable only on
  // can_view_passport_data (the person; an Owner/Manager/legacy Service Writer of a shop the person belongs to; the
  // Connect recruiting path) — NOT on the new read-only Passport audience (work-role advisors, ORG_ADMIN). Ask the
  // server that exact predicate for THIS viewer and leave the two sections out when it says no, instead of drawing
  // "section failed to load" and an Eligibility card of "—". Presentation only: RLS and the BKR4 chokepoint still
  // decide every row. A failed answer leaves the sections to their own (denied-is-not-empty) error reporting.
  const { data: hrPrivateEntitled, isError: hrPrivateUnknown } = useQuery({
    queryKey: ['passport-hr-private-entitled', profile?.user_id, viewer?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('can_view_passport_data', {
        _owner_id: profile!.user_id!,
        _viewer_id: viewer!.id,
      });
      if (error) throw error;
      return data === true;
    },
    enabled: !!profile?.user_id && !!viewer?.id && !isOwnProfile,
    staleTime: 60_000,
  });
  // Hidden while the answer is pending (no flash of a failure card); shown on true, and on a failed answer.
  const showHrPrivateSections = isOwnProfile || hrPrivateEntitled === true || hrPrivateUnknown;

  // Resolve technician type title
  const resolvedTitle = resolveTechnicianTypeTitle(
    profile as any,
    isOwnProfile ? passportProfile as any : undefined,
    proficiencies || [],
  );

  // Guardrail #2: Persist inferred title if computed differs from stored
  useEffect(() => {
    if (!profile || !proficiencies) return;
    if (!isOwnProfile && !isManagerView) return;
    if (!resolvedTitle.inferredTypeKey) return;
    if ((profile as any).technician_type_source === 'shop_verified') return;

    // Check if stored inferred value matches computed
    const storedInferred = (profile as any).technician_type_inferred;
    if (storedInferred === resolvedTitle.inferredTypeKey) return;

    // Persist the inferred title
    (supabase.from('technician_profiles') as any)
      .update({
        technician_type_inferred: resolvedTitle.inferredTypeKey,
        technician_type_inferred_at: clock.now().toISOString(),
        technician_type_source: 'inferred',
      })
      .eq('id', profile.id)
      .then();
  }, [profile?.id, resolvedTitle.inferredTypeKey, isOwnProfile, isManagerView]);

  // Core-100 R4 W2 (CORE-Q): someone else opening this Passport is recorded by the server in the read itself
  // (`open_technician_passport` above); the page no longer asks for it separately.

  if (profileLoading || isCheckingOwnership) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading technician profile...</p>
        </div>
      </div>
    );
  }

  const handleGoBack = () => {
    navigate('/');
  };

  if (!profile) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">Technician profile not found</p>
          <Button onClick={handleGoBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // Without a Passport-viewing capacity (e.g. a technician looking at a peer) only one's own Passport opens.
  if (role === 'TECHNICIAN' && !isOwnProfile && !isPassportViewer) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold mb-4">Access Restricted</h1>
          <p className="text-muted-foreground mb-4">
            You can only view your own Technician Passport
          </p>
          <Button onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-6 max-w-6xl pb-20 overflow-y-auto">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={handleGoBack}
        className="mb-2"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </Button>

      {/* Section 1: Snapshot Card */}
      <PassportSection id="snapshot-card" audiences={viewerAudiences}>
        <PassportSnapshotCard 
          profile={profile}
          proficiencies={proficiencies || []}
          metrics={metrics || null}
          technicianTypeTitle={resolvedTitle.displayTitle}
          technicianTypeSource={resolvedTitle.source}
          technicianTypeIsLocked={resolvedTitle.isLocked}
        />
      </PassportSection>

      {/* Pensieve coverage summary — Catalyst+ only */}
      <RequireFeature feature="technicianPassports">
        <PassportSection id="pensieve-summary" audiences={viewerAudiences}>
          <PassportPensieveSummary
            stats={pensieveQuery.data}
            className="mt-1"
          />
        </PassportSection>
      </RequireFeature>

      {/* Section 2: Employment History */}
      <PassportSection id="employment-history" audiences={viewerAudiences}>
        <PassportEmploymentHistory 
          employmentHistory={employmentHistory || []}
        />
      </PassportSection>

      {/* Section 2.5: Role Tracks (multi-role users) — V-F-1g Catalyst+ */}
      <RequireFeature feature="passportAdvancedIntelligence">
        <PassportSection id="role-tracks" audiences={viewerAudiences}>
          <PassportRoleTracks technicianProfileId={id!} />
        </PassportSection>
      </RequireFeature>

      {/* Section 3: Category Proficiencies */}
      <PassportSection id="proficiencies" audiences={viewerAudiences}>
        <div className="space-y-4">
          {/* Roadmap #9: background (what they bring) beside — never blended
              into — what OptiBay has observed. */}
          <TechnicianBackgroundCard
            shopId={(liveTech as { shop_id?: string } | null)?.shop_id ?? null}
            shopTechnicianId={liveTech?.id ?? null}
            optibaySince={(employmentHistory || []).reduce<string | null>(
              (min, e: any) => (e?.start_date && (!min || e.start_date < min) ? e.start_date : min), null)}
            completedJobs={metrics?.jobs_completed ?? null}
            canEdit={canEditBackground}
          />
          <PassportProficiencySection
            proficiencies={proficiencies || []}
          />
        </div>
      </PassportSection>

      {/* Section 3.5: Skill Heatmap */}
      <PassportSection id="skill-heatmap" audiences={viewerAudiences}>
        <PassportSkillHeatmap
          technicianProfileId={id}
          liveShopTechnicianId={liveTech?.id}
          liveProficiencies={
            (liveTech?.proficiencies as Record<string, number> | null) ?? null
          }
        />
      </PassportSection>

      {/* Section 4: Performance DNA — V-F-1g Catalyst+ */}
      <RequireFeature feature="passportAdvancedIntelligence">
        <PassportSection id="performance-dna" audiences={viewerAudiences}>
          <PassportPerformanceDNA
            technicianProfileId={id!}
            liveShopTechnicianId={liveTech?.id}
          />
        </PassportSection>
      </RequireFeature>

      {/* Section 4.5: Time Signature — V-F-1g Catalyst+ */}
      <RequireFeature feature="passportAdvancedIntelligence">
        <PassportSection id="time-signature" audiences={viewerAudiences}>
          <PassportTimeSignature
            technicianProfileId={id!}
          />
        </PassportSection>
      </RequireFeature>

      {/* Section 4.6: Inspection Performance */}
      <PassportSection id="inspection-metrics" audiences={viewerAudiences}>
        <PassportInspectionMetrics
          technicianProfileId={id!}
        />
      </PassportSection>

      {/* Section 4.75: OptiTime Learning (Pensieve) — Catalyst+ only */}
      <RequireFeature feature="technicianPassports">
        <PassportSection id="pensieve-insights" audiences={viewerAudiences}>
          <PassportPensieveInsights 
            stats={pensieveQuery.data}
            isLoading={pensieveQuery.isLoading}
          />
        </PassportSection>
      </RequireFeature>

      {/* Section 4.8: Coaching & Future Insights (Pensieve) — Catalyst+ only */}
      <RequireFeature feature="technicianPassports">
        <PassportSection id="pensieve-coaching" audiences={viewerAudiences}>
          <PassportPensieveCoaching
            stats={pensieveQuery.data}
            isLoading={pensieveQuery.isLoading}
          />
        </PassportSection>
      </RequireFeature>

      {/* Section 4.9: Diagnostic Competency — Catalyst+ Manager-Only */}
      <RequireFeature feature="pensieveLearning">
        <PassportSection id="diagnostic-competency" audiences={viewerAudiences}>
          <PassportDiagnosticCompetency
            technicianProfileId={id!}
            isManagerView={isManagerView}
            isSelfView={isOwnProfile}
            isInScopeViewer={!isOwnProfile && isPassportViewer}
          />
        </PassportSection>
      </RequireFeature>

      {/* Section 5: Ticket Ledger */}
      <PassportSection id="ticket-ledger" audiences={viewerAudiences}>
        <PassportTicketLedger
          technicianProfileId={id!}
        />
      </PassportSection>

      {/* Section 5.5: Toolbox (Private - technician-owned) */}
      <PassportSection id="toolbox" audiences={viewerAudiences}>
        <ToolboxSection
          technicianProfileId={id!}
        />
      </PassportSection>

      {/* Certifications */}
      <PassportSection id="certifications" audiences={viewerAudiences}>
        <PassportCertificationsSection
          userId={profile.user_id || ''}
          isOwner={isOwnProfile}
        />
      </PassportSection>

      {/* Application Sections */}
      <PassportSection id="experience" audiences={viewerAudiences}>
        <PassportExperienceSection userId={profile.user_id || ''} isOwner={isOwnProfile} isReadOnly={!isOwnProfile} attestForShop={!isOwnProfile && isManagerView && activeShop ? { id: activeShop.id, name: activeShop.name } : null} />
      </PassportSection>
      <PassportSection id="education" audiences={viewerAudiences}>
        <PassportEducationSection userId={profile.user_id || ''} isOwner={isOwnProfile} isReadOnly={!isOwnProfile} />
      </PassportSection>
      {showHrPrivateSections && (
        <>
          <PassportSection id="references" audiences={viewerAudiences}>
            <PassportReferencesSection userId={profile.user_id || ''} isOwner={isOwnProfile} isReadOnly={!isOwnProfile} />
          </PassportSection>
          <PassportSection id="eligibility" audiences={viewerAudiences}>
            <PassportEligibilitySection userId={profile.user_id || ''} isOwner={isOwnProfile} isReadOnly={!isOwnProfile} />
          </PassportSection>
        </>
      )}

      {/* Section 6: Future Insights — V-F-1g Catalyst+ */}
      <RequireFeature feature="passportAdvancedIntelligence">
        <PassportSection id="future-insights" audiences={viewerAudiences}>
          <PassportFutureInsights
            proficiencies={proficiencies || []}
            metrics={metrics || null}
          />
        </PassportSection>
      </RequireFeature>

      {/* Section 7: Learning Center */}
      <PassportSection id="learning-center" audiences={viewerAudiences}>
        <PassportLearningCenter
          technicianProfileId={id!}
          proficiencies={proficiencies || []}
          isOwnProfile={isOwnProfile}
        />
      </PassportSection>

      {/* Section 8: Coaching Notes — V-F-1g Catalyst+ */}
      <RequireFeature feature="passportAdvancedIntelligence">
        <PassportSection id="coaching-notes" audiences={viewerAudiences}>
          <PassportCoachingNotes
            technicianProfileId={id!}
            initialNotes={profile.notes}
            isManagerView={isManagerView}
          />
        </PassportSection>
      </RequireFeature>
    </div>
  );
}
